import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Loader2, Package, Search } from "lucide-react";
import api from "../utils/api";

interface AssetsTabProps {
    userId: string;
}

interface Asset {
    id: string;
    name: string;
    tag: string;
    type: string;
    status: string;
    condition: string;
    createdAt: string;
}

export const AssetsTab: React.FC<AssetsTabProps> = ({ userId }) => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await api.get(`/api/assets?assignedToUserId=${userId}`);
                // response is the assets array itself or wrapped in an object depending on the api response format.
                // In backend/routes/asset.routes.js, it returns: `res.json(signedAssets);`
                const assetsData = Array.isArray(response) ? response : (response as any).data || [];
                setAssets(assetsData);
            } catch (err) {
                console.error("Error fetching assets:", err);
                setError("Failed to load assets.");
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchAssets();
        }
    }, [userId]);

    const filteredAssets = useMemo(() => {
        return assets.filter((asset) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                asset.name?.toLowerCase().includes(searchLower) ||
                asset.tag?.toLowerCase().includes(searchLower) ||
                asset.type?.toLowerCase().includes(searchLower)
            );
        });
    }, [assets, searchTerm]);

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return <div className="text-red-500 p-4">{error}</div>;
    }

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search assets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="rounded-md border">
                {/* Mobile View */}
                <div className="md:hidden space-y-0 divide-y">
                    {filteredAssets.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                            <p>No assets found.</p>
                        </div>
                    ) : (
                        filteredAssets.map((asset) => (
                            <div key={asset.id} className="p-4 space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <Link
                                            to={`/assets/${asset.id}`}
                                            className="font-medium text-sm text-primary hover:underline block truncate"
                                        >
                                            {asset.name}
                                        </Link>
                                    </div>
                                    <Badge variant="outline" className="text-[10px] whitespace-nowrap">
                                        {asset.status}
                                    </Badge>
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                    <span>Tag: {asset.tag}</span>
                                    <span>•</span>
                                    <span>Type: {asset.type}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Asset Name</TableHead>
                                <TableHead>Tag</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Condition</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredAssets.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        No assets assigned.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredAssets.map((asset) => (
                                    <TableRow key={asset.id} className="hover:bg-slate-50">
                                        <TableCell>
                                            <Link
                                                to={`/assets/${asset.id}`}
                                                className="font-medium text-sm hover:underline text-primary flex items-center gap-2"
                                            >
                                                <Package size={16} className="text-muted-foreground" />
                                                {asset.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="font-mono text-xs">
                                                {asset.tag}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{asset.type}</TableCell>
                                        <TableCell>
                                            <Badge variant={asset.status === "Active" ? "default" : "outline"} className="text-xs">
                                                {asset.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{asset.condition || "-"}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default AssetsTab;
